import React, { useMemo, useCallback } from 'react'
import { ScoopSpinner, ScoopTree } from '../../../../ui'
import ColumnItem from './ColumnItem'
import { useExplorerDataTab } from './useExplorerDataTab'
import { useExplorer } from '../../../../../context/explorer'
import { useModalManager, MODALS } from '../../../../modals'
import styles from './DataTab.module.scss'

/**
 * DataTab component for selecting metrics in Explorer.
 *
 * This component uses ExplorerContext via useExplorer() and useExplorerDataTab().
 * It no longer receives props - all state comes from context.
 *
 * Now uses Modal Manager for dialog state management.
 * Dialogs are rendered via ModalOutlet, not embedded here.
 *
 * Key changes from old implementation:
 * - No props (inboxes, summaryConfig, setSummaryConfig removed)
 * - Uses useExplorer() for workspace data and actions
 * - Uses useExplorerDataTab() for tree data and UI state
 * - Uses Modal Manager for dialog management (no local dialog state)
 * - Uses ScoopTree for tree rendering
 * - ColumnItem uses context actions directly
 */
export const DataTab = () => {
    // Get workspace data, loading state, and metric actions from context
    const { workspaceLoading, dataConfig, updateMetric, workspaceMetadata } = useExplorer()

    // Modal Manager for dialog control
    const { showModal } = useModalManager()

    // Get tree data and search from hook (no dialog state needed)
    const { displayTreeData, countSelectedInTree, setSearchTerm } = useExplorerDataTab()

    /**
     * Opens a dialog for editing a specific metric using Modal Manager.
     * @param {string} dialogType - 'format' | 'aggregation' | 'filter' | 'period'
     * @param {number} metricIndex - Index of the metric in selectedItems
     */
    const handleOpenDialog = useCallback(
        async (dialogType, metricIndex) => {
            const modalMap = {
                format: MODALS.EXPLORER.FORMAT,
                filter: MODALS.EXPLORER.FILTER,
                period: MODALS.EXPLORER.PERIOD,
                aggregation: MODALS.EXPLORER.AGGREGATION,
            }

            const modalType = modalMap[dialogType]
            if (modalType) {
                // Pass context data to modals since they render outside ExplorerProvider
                const selectedMetric = dataConfig.selectedItems[metricIndex]
                await showModal(modalType, {
                    metricIndex,
                    selectedMetric,
                    updateMetric,
                    workspaceMetadata,
                })
            }
        },
        [showModal, dataConfig.selectedItems, updateMetric, workspaceMetadata]
    )

    // Transform displayTreeData to ScoopTree format
    const treeData = useMemo(() => {
        if (!displayTreeData?.children) return []
        return displayTreeData.children
    }, [displayTreeData])

    // Custom node renderer for the tree
    const renderNode = useCallback(
        (node) => {
            // Column nodes get rendered as ColumnItem
            if (node.type === 'column') {
                return <ColumnItem node={node} onOpenDialog={handleOpenDialog} />
            }

            // Dataset/folder nodes show name with selection count badge
            const selectedCount = countSelectedInTree(node)
            const showCounter = node.type === 'dataset' && selectedCount > 0

            return (
                <div className={styles.treeNodeLabel}>
                    <span className={styles.treeNodeText}>{node.name}</span>
                    {showCounter && <span className={styles.selectedCounter}>{selectedCount}</span>}
                </div>
            )
        },
        [countSelectedInTree, handleOpenDialog]
    )

    // Loading state
    if (workspaceLoading) {
        return (
            <div className={styles.loadingContainer}>
                <ScoopSpinner size={40} />
            </div>
        )
    }

    return (
        <div className={styles.dataTabContainer}>
            <ScoopTree
                data={treeData}
                searchable
                searchPlaceholder="Search"
                searchHeight="36px"
                searchWidth="100%"
                onSearch={setSearchTerm}
                renderNode={renderNode}
                nodeIdKey="id"
                nodeLabelKey="name"
                nodeChildrenKey="children"
                className={styles.tree}
            />
            {/* Dialogs are now rendered via ModalOutlet - no embedded dialogs here */}
        </div>
    )
}
